import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/index.css";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Header, Footer, WhatsAppWidget } from "@/components/site/Layout";
import { ConciergeChat } from "@/components/site/ConciergeChat";
import { Hero } from "@/components/site/Hero";
import { Manifesto, PhotoStrip, Rooms, Suite, Rooftopz, Cafe, Ribbon, Banquet, WhyDirect, OfferBanner, Gallery, Location, Faq } from "@/components/site/Sections";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import AdminApp from "@/admin/AdminApp";
import { HOTEL, FAQS, REVIEWS, GALLERY } from "@/data";

const SITE_URL = "https://www.hotelvirasatretreat.co.in";

const HOTEL_NODE = {
  "@type": ["Hotel", "LocalBusiness"],
  "@id": `${SITE_URL}/#hotel`,
  name: "Hotel Virasat Retreat",
  description:
    "A family-owned 16-room boutique hotel in Patna, opposite Medanta Super Speciality Hospital in a serene residential area, with ROOFTOPz Botanical Restaurant, Tea Toast café and a 3rd-floor banquet hall.",
  slogan: "Traditional Hospitality, Modern Facilities",
  url: SITE_URL,
  telephone: HOTEL.phone,
  email: HOTEL.email,
  image: GALLERY.slice(0, 6).map((g) => g.url),
  sameAs: [
    "https://www.facebook.com/share/1S5rP7CySv/",
    "https://www.instagram.com/hotelvirasatretreat3",
  ],
  priceRange: "₹₹",
  currenciesAccepted: "INR",
  starRating: { "@type": "Rating", ratingValue: "4" },
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.3", reviewCount: "228" },
  address: {
    "@type": "PostalAddress",
    streetAddress: HOTEL.addressLine,
    addressLocality: "Patna",
    addressRegion: "Bihar",
    postalCode: HOTEL.pincode,
    addressCountry: "IN",
  },
  amenityFeature: [
    "Free Wi-Fi", "Air Conditioning", "Rooftop Restaurant", "Free Parking",
    "24x7 Front Desk", "Room Service", "Banquet Hall", "Power Backup",
  ].map((n) => ({ "@type": "LocationFeatureSpecification", name: n, value: true })),
  checkinTime: "12:00", checkoutTime: "11:00",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "00:00", closes: "23:59",
  },
};

const ORG_NODE = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Hotel Virasat Retreat",
  url: SITE_URL,
  logo: HOTEL.logo,
  sameAs: [
    "https://www.facebook.com/share/1S5rP7CySv/",
    "https://www.instagram.com/hotelvirasatretreat3",
  ],
};

const FAQ_NODE = {
  "@type": "FAQPage",
  "@id": `${SITE_URL}/#faq`,
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const REVIEW_NODES = REVIEWS.map((r) => ({
  "@type": "Review",
  itemReviewed: { "@id": `${SITE_URL}/#hotel` },
  author: { "@type": "Person", name: r.name },
  reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
  reviewBody: r.text,
  publisher: { "@type": "Organization", name: r.source },
}));

const BREADCRUMB_NODE = {
  "@type": "BreadcrumbList",
  itemListElement: [
    ["Home", "/"], ["Rooms", "/#rooms"], ["ROOFTOPz Restaurant", "/#rooftopz"],
    ["Banquet & Events", "/#banquet"], ["Gallery", "/#gallery"], ["Contact", "/#enquiry"],
  ].map(([name, path], i) => ({
    "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}`,
  })),
};

const SEO_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [HOTEL_NODE, ORG_NODE, FAQ_NODE, BREADCRUMB_NODE, ...REVIEW_NODES],
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/*" element={<MarketingHome />} />
      </Routes>
    </BrowserRouter>
  );
}

function MarketingHome() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);

    // smooth anchor navigation
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (!id || id.length <= 1 || id === "#top") return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      const doScroll = () => {
        try { lenis.scrollTo(el, { offset: -60, duration: 1.0 }); }
        catch { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
      };
      doScroll();
      // Re-correct as lazy images grow the page after click
      [1200, 2000, 2800, 3800].forEach((delay) => setTimeout(() => {
        const r = el.getBoundingClientRect();
        if (Math.abs(r.top - 60) > 40) doScroll();
      }, delay));
    };
    document.addEventListener("click", onClick);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; document.removeEventListener("click", onClick); };
  }, []);

  return (
    <div className="App grain">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SEO_SCHEMA) }} />
      <Header />
      <main>
        <Hero />
        <PhotoStrip />
        <Manifesto />
        <Rooms />
        <Suite />
        <Rooftopz />
        <Cafe />
        <Ribbon />
        <Banquet />
        <WhyDirect />
        <OfferBanner />
        <Gallery />
        <Location />
        <Faq />
        <EnquiryForm />
      </main>
      <Footer />
      <WhatsAppWidget />
      <ConciergeChat />
      <Toaster position="bottom-center" richColors />
    </div>
  );
}

export default App;
